import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useCalls, type ActivitySubject } from '../../lib/activity/use-activity.js';
import { CallList } from '../activity/call-list.js';
import { LogCallDialog } from '../activity/log-call-dialog.js';
import { Button, ListSkeleton } from '@tailoring-crm/ui';

/**
 * Every call the shop has had with this customer, newest first, and the button that logs another.
 *
 * Logging only: a call is what HAPPENED, so the list offers no edit and no delete — the server
 * refuses both. Deciding when to ring next is a follow-up, on the tab beside this one.
 */
export function CustomerCallsTab({
  customerId,
  customerName,
}: {
  customerId: string;
  customerName: string;
}): ReactElement {
  const { t } = useTranslation();
  const subject: ActivitySubject = { kind: 'customer', id: customerId };
  const calls = useCalls(subject);
  const [logging, setLogging] = useState(false);

  return (
    <section className="activity-panel flex flex-col gap-4 border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">{t('activity.calls.heading')}</h3>
        <Button variant="outline" size="sm" onClick={() => setLogging(true)}>
          <Plus aria-hidden="true" className="size-4" />
          {t('activity.calls.new')}
        </Button>
      </div>

      {calls.isPending ? <ListSkeleton rows={3} /> : <CallList calls={calls.data?.items ?? []} />}

      <LogCallDialog
        open={logging}
        onOpenChange={setLogging}
        subject={subject}
        name={customerName}
      />
    </section>
  );
}
